import { AfterViewInit, Component, DestroyRef, ElementRef, OnDestroy, OnInit, ViewChild, effect, input, output } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { Chart, ChartConfiguration, registerables } from 'chart.js';
import { ChoroplethController, ColorScale, GeoFeature, ProjectionScale, topojson } from 'chartjs-chart-geo';
import type { Feature, FeatureCollection, GeoJsonProperties, Geometry } from 'geojson';
import type { GeometryCollection, Topology } from 'topojson-specification';

import { LayoutService } from '@/layout/service/layout.service';

Chart.register(...registerables, ChoroplethController, GeoFeature, ColorScale, ProjectionScale);

export type EstadoSelecionado = {
    uf?: string;
    nome: string;
};

type GeoJsonPropertiesWithName = GeoJsonProperties & { name?: string };

type GeoJsonFeature = Feature<Geometry, GeoJsonPropertiesWithName>;

type GeoJsonFeatureCollection = FeatureCollection<Geometry, GeoJsonPropertiesWithName>;

type GeoDataPoint = {
    feature: GeoJsonFeature;
    value: number;
    valueOf: () => number;
};

type BrasilTopology = Topology<{ bra: GeometryCollection<GeoJsonPropertiesWithName> }>;

type CorRgb = {
    r: number;
    g: number;
    b: number;
};

@Component({
    selector: 'app-mapa-uf',
    standalone: true,
    imports: [],
    templateUrl: './mapa-uf.html',
    styleUrl: './mapa-uf.scss'
})
export class MapaUf implements AfterViewInit, OnDestroy, OnInit {
    @ViewChild('mapaBrasilCanvas', { static: true })
    private canvasRef!: ElementRef<HTMLCanvasElement>;

    estadoSelecionado = output<EstadoSelecionado | null>();
    ufSelecionada = input<string | null>(null);

    private chart: Chart<'choropleth', GeoDataPoint[]> | null = null;
    private abortController: AbortController | null = null;
    private geoJsonBrasil: GeoJsonFeatureCollection | null = null;
    private geoDataBrasil: GeoJsonFeature[] = [];
    private dataPointsBrasil: GeoDataPoint[] = [];
    private dataPointsEstado: GeoDataPoint[] = [];
    private estadoAtual: GeoJsonFeature | null = null;
    private corPrimariaRgb: CorRgb | null = null;
    private deveAtualizarEscala = false;
    private possuiUfExternaPendente = false;
    private ufExternaPendente: string | null = null;
    private readonly ufPorEstado = new Map<string, string>([
        ['acre', 'AC'],
        ['alagoas', 'AL'],
        ['amapa', 'AP'],
        ['amazonas', 'AM'],
        ['bahia', 'BA'],
        ['ceara', 'CE'],
        ['distrito federal', 'DF'],
        ['espirito santo', 'ES'],
        ['goias', 'GO'],
        ['maranhao', 'MA'],
        ['mato grosso', 'MT'],
        ['mato grosso do sul', 'MS'],
        ['minas gerais', 'MG'],
        ['para', 'PA'],
        ['paraiba', 'PB'],
        ['parana', 'PR'],
        ['pernambuco', 'PE'],
        ['piaui', 'PI'],
        ['rio de janeiro', 'RJ'],
        ['rio grande do norte', 'RN'],
        ['rio grande do sul', 'RS'],
        ['rondonia', 'RO'],
        ['roraima', 'RR'],
        ['santa catarina', 'SC'],
        ['são paulo', 'SP'],
        ['sergipe', 'SE'],
        ['tocantins', 'TO']
    ]);

    constructor(
        private layoutService: LayoutService,
        private destroyRef: DestroyRef
    ) {
        effect(() => {
            const uf = this.normalizarUfEntrada(this.ufSelecionada());
            if (!this.geoJsonBrasil) {
                this.ufExternaPendente = uf;
                this.possuiUfExternaPendente = true;
                return;
            }

            this.aplicarSelecaoExterna(uf);
        });
    }

    ngOnInit(): void {
        this.layoutService.configUpdate$.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(() => {
            this.atualizarEscalaCores();
        });
    }

    ngAfterViewInit(): void {
        this.canvasRef.nativeElement.addEventListener('click', this.handleMapaClick);
        void this.carregarMapa();
    }

    ngOnDestroy(): void {
        this.canvasRef.nativeElement.removeEventListener('click', this.handleMapaClick);
        this.abortController?.abort();
        this.chart?.destroy();
    }

    private async carregarMapa(): Promise<void> {
        if (!this.canvasRef?.nativeElement) {
            return;
        }

        this.abortController?.abort();
        this.abortController = new AbortController();

        const response = await fetch('geo/bra.topo.json', { signal: this.abortController.signal });

        if (!response.ok) {
            return;
        }

        const brasil = (await response.json()) as BrasilTopology;
        const geoJson = topojson.feature(brasil, brasil.objects.bra) as GeoJsonFeatureCollection;
        const geoData = geoJson.features ?? [];
        const dataPoints = this.criarDataPoints(geoData);

        this.geoJsonBrasil = geoJson;
        this.geoDataBrasil = geoData;
        this.dataPointsBrasil = dataPoints;
        this.dataPointsEstado = [];
        this.estadoAtual = null;

        const corBase = this.obterCorPrimariaRgb();
        const config: ChartConfiguration<'choropleth', GeoDataPoint[]> = {
            type: 'choropleth',
            data: {
                labels: geoData.map((item) => item.properties?.name ?? ''),
                datasets: [
                    {
                        label: 'Estados',
                        outline: geoJson.features,
                        showOutline: true,
                        xAxisID: 'projection',
                        yAxisID: 'projection',
                        data: dataPoints
                    }
                ]
            },
            options: {
                responsive: true,
                maintainAspectRatio: false,
                interaction: {
                    mode: 'nearest',
                    intersect: true
                },
                plugins: {
                    legend: {
                        display: false
                    },
                    tooltip: {
                        callbacks: {
                            title: (items) => (items[0]?.raw as GeoDataPoint | undefined)?.feature?.properties?.name ?? '',
                            label: () => ''
                        }
                    }
                },
                elements: {
                    geoFeature: {
                        borderWidth: (context) => (context.active ? 2.5 : 0.6),
                        borderColor: (context) => (context.active ? this.obterCorBordaAtiva() : this.obterCorBordaInativa())
                    }
                },
                scales: {
                    color: {
                        type: 'color',
                        axis: 'x',
                        display: false,
                        interpolate: (valor) => this.interpolarCorPrimaria(valor, corBase)
                    },
                    projection: {
                        type: 'projection',
                        projection: 'mercator'
                    }
                }
            }
        };

        this.chart?.destroy();
        this.chart = new Chart(this.canvasRef.nativeElement, config);

        if (this.possuiUfExternaPendente) {
            this.possuiUfExternaPendente = false;
            this.aplicarSelecaoExterna(this.ufExternaPendente);
        } else {
            this.aplicarSelecaoExterna(this.normalizarUfEntrada(this.ufSelecionada()));
        }

        if (this.deveAtualizarEscala) {
            this.deveAtualizarEscala = false;
            this.atualizarEscalaCores();
        }
    }

    private criarDataPoints(geoData: GeoJsonFeature[]): GeoDataPoint[] {
        return geoData.map((feature) => {
            const value = Math.floor(Math.random() * 100) + 1;
            return {
                feature,
                value,
                valueOf: () => value
            };
        });
    }

    private atualizarEscalaCores(): void {
        if (!this.chart) {
            this.deveAtualizarEscala = true;
            return;
        }

        requestAnimationFrame(() => {
            if (!this.chart) {
                return;
            }

            this.corPrimariaRgb = null;
            const corBase = this.obterCorPrimariaRgb();
            const escalaCor = this.chart.options.scales?.['color'];

            if (!escalaCor || typeof escalaCor !== 'object') {
                return;
            }

            (escalaCor as { interpolate?: (valor: number) => string }).interpolate = (valor) => this.interpolarCorPrimaria(valor, corBase);
            this.chart.update();
        });
    }

    private obterCorBordaInativa(): string {
        const corBase = this.obterCorPrimariaRgb();
        const corBorda = this.misturarCores(corBase, { r: 255, g: 255, b: 255 }, 0.55);
        return `rgb(${corBorda.r}, ${corBorda.g}, ${corBorda.b})`;
    }

    private obterCorBordaAtiva(): string {
        const corBase = this.obterCorPrimariaRgb();
        const corBorda = this.misturarCores(corBase, { r: 0, g: 0, b: 0 }, 0.2);
        return `rgb(${corBorda.r}, ${corBorda.g}, ${corBorda.b})`;
    }

    private obterCorPrimariaRgb(): CorRgb {
        if (this.corPrimariaRgb) {
            return this.corPrimariaRgb;
        }

        const estilos = getComputedStyle(document.documentElement);
        const corPrimaria = estilos.getPropertyValue('--p-primary-color').trim() || estilos.getPropertyValue('--primary-color').trim() || '#3b82f6';

        this.corPrimariaRgb = this.parsearCorParaRgb(corPrimaria);
        return this.corPrimariaRgb;
    }

    private interpolarCorPrimaria(valor: number, corBase: CorRgb): string {
        const normalizado = Math.min(Math.max(valor, 0), 1);
        const corClara = this.misturarCores(corBase, { r: 255, g: 255, b: 255 }, 0.75);
        const corEscura = this.misturarCores(corBase, { r: 0, g: 0, b: 0 }, 0.35);

        if (normalizado <= 0.5) {
            const interpolado = this.misturarCores(corClara, corBase, normalizado / 0.5);
            return `rgb(${interpolado.r}, ${interpolado.g}, ${interpolado.b})`;
        }

        const interpolado = this.misturarCores(corBase, corEscura, (normalizado - 0.5) / 0.5);
        return `rgb(${interpolado.r}, ${interpolado.g}, ${interpolado.b})`;
    }

    private misturarCores(corA: CorRgb, corB: CorRgb, fator: number): CorRgb {
        const t = Math.min(Math.max(fator, 0), 1);
        return {
            r: Math.round(corA.r + (corB.r - corA.r) * t),
            g: Math.round(corA.g + (corB.g - corA.g) * t),
            b: Math.round(corA.b + (corB.b - corA.b) * t)
        };
    }

    private parsearCorParaRgb(cor: string): CorRgb {
        const valor = cor.trim();
        if (valor.startsWith('#')) {
            const hex = valor.slice(1);
            if (hex.length === 3) {
                const r = parseInt(hex[0] + hex[0], 16);
                const g = parseInt(hex[1] + hex[1], 16);
                const b = parseInt(hex[2] + hex[2], 16);
                return { r, g, b };
            }
            if (hex.length === 6) {
                const r = parseInt(hex.slice(0, 2), 16);
                const g = parseInt(hex.slice(2, 4), 16);
                const b = parseInt(hex.slice(4, 6), 16);
                return { r, g, b };
            }
        }

        const match = valor.match(/rgba?\((\d+),\s*(\d+),\s*(\d+)/);
        if (match) {
            return {
                r: Number.parseInt(match[1], 10),
                g: Number.parseInt(match[2], 10),
                b: Number.parseInt(match[3], 10)
            };
        }

        return { r: 59, g: 130, b: 246 };
    }

    private handleMapaClick = (event: MouseEvent) => {
        if (!this.chart) {
            return;
        }

        const elementos = this.chart.getElementsAtEventForMode(event, 'nearest', { intersect: true }, true);

        if (elementos.length === 0) {
            if (this.estadoAtual) {
                this.exibirBrasil();
            }
            return;
        }

        const indice = elementos[0]?.index ?? -1;
        const pontos = this.estadoAtual ? this.dataPointsEstado : this.dataPointsBrasil;
        const feature = pontos[indice]?.feature;

        if (!feature) {
            return;
        }

        if (this.estadoAtual && this.estadoAtual === feature) {
            this.exibirBrasil();
            return;
        }

        this.exibirEstado(feature);
    };

    private exibirEstado(feature: GeoJsonFeature, emitirEvento = true): void {
        this.estadoAtual = feature;
        const dataPoints = this.criarDataPoints([feature]);
        this.dataPointsEstado = dataPoints;
        const nomeEstado = feature.properties?.name ?? 'Estado';
        const uf = this.obterUfPorNome(nomeEstado);
        if (emitirEvento) {
            this.estadoSelecionado.emit({ nome: nomeEstado, uf });
        }
        this.atualizarMapa([feature], dataPoints);
    }

    private exibirBrasil(emitirEvento = true): void {
        if (!this.geoJsonBrasil) {
            return;
        }

        this.estadoAtual = null;
        this.dataPointsEstado = [];
        if (emitirEvento) {
            this.estadoSelecionado.emit(null);
        }
        this.atualizarMapa(this.geoDataBrasil, this.dataPointsBrasil);
    }

    private atualizarMapa(geoData: GeoJsonFeature[], dataPoints: GeoDataPoint[]): void {
        if (!this.chart) {
            return;
        }

        this.chart.data.labels = geoData.map((item) => item.properties?.name ?? '');

        const dataset = this.chart.data.datasets[0];
        dataset.data = dataPoints;
        (dataset as unknown as { outline: GeoJsonFeature[] }).outline = geoData;
        this.chart.update();
    }

    private obterUfPorNome(nomeEstado: string): string | undefined {
        const normalizado = this.normalizarNomeEstado(nomeEstado);
        return this.ufPorEstado.get(normalizado);
    }

    private normalizarNomeEstado(nomeEstado: string): string {
        return nomeEstado
            .normalize('NFD')
            .replace(/[\u0300-\u036f]/g, '')
            .toLowerCase();
    }

    private normalizarUfEntrada(uf: string | null | undefined): string | null {
        if (!uf || uf === 'BR') {
            return null;
        }

        return uf.trim().toUpperCase();
    }

    private aplicarSelecaoExterna(uf: string | null): void {
        if (!this.geoJsonBrasil) {
            return;
        }

        const ufAtual = this.obterUfAtual();
        if (ufAtual === uf) {
            return;
        }

        if (!uf) {
            if (this.estadoAtual) {
                this.exibirBrasil(false);
            }
            return;
        }

        const feature = this.encontrarFeaturePorUf(uf);
        if (!feature) {
            if (this.estadoAtual) {
                this.exibirBrasil(false);
            }
            return;
        }

        this.exibirEstado(feature, false);
    }

    private obterUfAtual(): string | null {
        if (!this.estadoAtual) {
            return null;
        }

        const nomeEstado = this.estadoAtual.properties?.name ?? '';
        return this.obterUfPorNome(nomeEstado) ?? null;
    }

    private encontrarFeaturePorUf(uf: string): GeoJsonFeature | null {
        if (!this.geoDataBrasil.length) {
            return null;
        }

        return (
            this.geoDataBrasil.find((feature) => {
                const nomeEstado = feature.properties?.name ?? '';
                return this.obterUfPorNome(nomeEstado) === uf;
            }) ?? null
        );
    }
}
